import {ReadinessIcon,ReadinessNavLink} from './BuildingReadiness';
import {WorkspaceMoreLinks} from './WorkspaceMoreLinks';
import {useEffect,useState} from 'react';
import {useLocation} from 'react-router';
import {type IconName} from './Icon';
import {useWorkspaceSession} from './StaffSession';
import {withBuildingScope} from './building-scope';
import {permittedWorkspaces} from './workspace-catalogue';
import type {WorkspaceNavigation} from './area-navigation';

type Counts={building:string|null;queue_counts?:Record<string,number>;new_count?:number;drafts_to_review?:number;overdue?:number;assigned_to_me?:number};

/** Mail keeps its folders on every screen. Counts reuse the canonical header/inbox
 * summary, rather than making a second mailbox-count request or inventing unread totals. */
export function MailWorkspaceRail({navigation}:{navigation:NonNullable<WorkspaceNavigation['tertiary']>}){
 const session=useWorkspaceSession()!,location=useLocation();
 const building=session.effectiveScope.kind==='building'?session.effectiveScope.id:undefined;
 const [counts,setCounts]=useState<Counts>();
 useEffect(()=>{
  setCounts(undefined);
  const receive=(event:Event)=>{
   const next=(event as CustomEvent<Counts>).detail;
   if(next?.building===(building??null))setCounts(next);
  };
  window.addEventListener('blockgenie:mail-folder-summary',receive);
  return()=>window.removeEventListener('blockgenie:mail-folder-summary',receive);
 },[building]);
 const currentCounts=counts?.building===(building??null)?counts:undefined;
 const query=new URLSearchParams(location.search),queue=query.get('queue');
 const scoped=(path:string)=>withBuildingScope(path,building);
 const folder=(path:string)=>{const url=new URL(scoped(path),'https://staff.invalid'),account=query.get('accountId');if(account)url.searchParams.set('accountId',account);return url.pathname+url.search;};
 const items:{label:string;href:string;icon:IconName;count?:number|undefined;active:boolean}[]=[
  ...(permittedWorkspaces(session).some(item=>item.code==='communications')?[{label:'Overview',href:scoped('/engines/communications'),icon:'layout-dashboard' as const,active:location.pathname==='/engines/communications'}]:[]),
  {label:'Inbox',href:folder('/communications'),icon:'inbox',count:currentCounts?.new_count,active:location.pathname.startsWith('/communications/threads/')||location.pathname==='/communications'&&!queue},
  {label:'Drafts',href:folder('/communications?queue=drafts'),icon:'file-text',count:currentCounts?.queue_counts?.drafts,active:location.pathname==='/communications'&&queue==='drafts'},
  ...(session.capabilities.includes('communications:review-proposals')?[{label:'Needs review',href:scoped('/communications/review'),icon:'clipboard-check' as const,active:location.pathname==='/communications/review'}]:[]),
  {label:'Sent & activity',href:folder('/communications?queue=sent'),icon:'arrow-up',active:location.pathname==='/communications'&&queue==='sent'},
  {label:'Handled history',href:scoped('/communications/handled'),icon:'clock',active:location.pathname==='/communications/handled'},
  {label:'Mailbox tools',href:'/communications/tools',icon:'settings',active:location.pathname==='/communications/tools'},
 ];
 const seen=new Set(items.map(item=>item.href));
 const extra=navigation.sections.flatMap(section=>section.items).filter(item=>!seen.has(item.href));
 return <nav className="workspace-family-navigation mail-family-navigation" aria-label="Mail folders"><ul>{items.map(item=><li key={item.label}>
  <ReadinessNavLink to={item.href} aria-current={item.active?'page':undefined} aria-label={item.label}>
   <ReadinessIcon name={item.icon} href={item.href}/><span>{item.label}</span>
   {['Inbox','Drafts'].includes(item.label)&&<small title={item.label==='Inbox'?'New conversations; not an unread count':'Conversations with active drafts'}>{Number.isSafeInteger(item.count)&&item.count!>=0?item.count:'—'}</small>}
  </ReadinessNavLink>
 </li>)}</ul>
  <WorkspaceMoreLinks items={extra} isCurrent={item=>navigation.active===item.id} className="mail-family-navigation__more"/>
 </nav>;
}
